import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { fetchCertificationSettings, fetchOrganizationSettings } from '../services/settingsAPI';

/**
 * Génère un certificat PDF en utilisant un template personnalisé ou le design par défaut
 * @param {Object} certificationData - Données du certificat
 * @returns {Promise<jsPDF>} Document PDF
 */
export const generateCertificatePDF = async (certificationData) => {
  let orgName = 'FORMAPRO';
  try {
    const org = await fetchOrganizationSettings();
    orgName = org?.gymName || orgName;
  } catch {
    /* default watermark */
  }

  const dataWithOrg = { ...certificationData, organizationName: orgName };

  try {
    const { templates, activeTemplateId } = await fetchCertificationSettings();
    if (activeTemplateId) {
      const activeTemplate = templates.find((t) => t.id === activeTemplateId);
      if (activeTemplate?.data) {
        return await generateCertificateWithCustomTemplate(dataWithOrg, activeTemplate);
      }
    }
  } catch (err) {
    console.warn('Certification template load failed, using default design:', err);
  }

  return generateDefaultCertificatePDF(dataWithOrg);
};

/**
 * Génère un certificat en utilisant un template personnalisé
 * @param {Object} certificationData - Données du certificat
 * @param {Object} template - Template personnalisé
 * @returns {jsPDF} Document PDF
 */
const generateCertificateWithCustomTemplate = async (certificationData, template) => {
  // Normaliser les données
  const data = {
    certificateType: 'certification',
    studentName: 'Étudiant',
    certificateName: 'Certificat de formation',
    issueDate: new Date().toISOString(),
    expirationDate: null,
    score: null,
    instructorName: 'Formateur',
    instructorId: '',
    id: '',
    remarks: '',
    ...certificationData
  };

  try {
    // Créer le document en paysage (A4)
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    // Convertir le PDF template en image canvas
    const templateImageData = await convertPdfToImage(template.data);
    
    if (templateImageData) {
      // Ajouter l'image du template comme fond
      doc.addImage(templateImageData, 'PNG', 0, 0, pageWidth, pageHeight);
    }
    
    // Superposer les données du certificat directement sur la même page
    await overlayDataOnTemplate(doc, data, pageWidth, pageHeight);

    return doc;
  } catch (error) {
    // Fallback vers le design par défaut en cas d'erreur
    return generateDefaultCertificatePDF(certificationData);
  }
};

/**
 * Convertit un PDF base64 en image utilisable par jsPDF
 * @param {string} base64Data - Données base64 du PDF
 * @returns {Promise<string>} Image canvas en base64
 */
const convertPdfToImage = async (base64Data) => {
  try {
    // Utiliser PDF.js pour convertir le PDF en image
    const pdfjsLib = window.pdfjsLib;
    
    if (!pdfjsLib) {
      return null;
    }

    if (pdfjsLib.GlobalWorkerOptions) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
    }

    // Convertir base64 en Uint8Array
    const binaryString = atob(base64Data.split(',')[1]);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Charger le PDF et rendre la première page
    const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
    const page = await pdf.getPage(1);

    const scale = 2; // Haute résolution
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    await page.render({
      canvasContext: context,
      viewport: viewport
    }).promise;

    return canvas.toDataURL('image/png');
  } catch (error) {
    return null;
  }
};

/**
 * Superpose les données de certification sur le template
 * @param {jsPDF} doc - Document PDF
 * @param {Object} data - Données de certification
 * @param {number} pageWidth - Largeur de la page
 * @param {number} pageHeight - Hauteur de la page
 */
const overlayDataOnTemplate = async (doc, data, pageWidth, pageHeight) => {
  // Positions prédéfinies pour les données (peuvent être ajustées)
  const positions = {
    studentName: { x: pageWidth / 2, y: pageHeight * 0.45 },
    certificateName: { x: pageWidth / 2, y: pageHeight * 0.6 },
    issueDate: { x: pageWidth * 0.25, y: pageHeight * 0.8 },
    instructorName: { x: pageWidth * 0.75, y: pageHeight * 0.8 },
    certificateId: { x: pageWidth / 2, y: pageHeight * 0.95 },
    expirationDate: { x: pageWidth * 0.5, y: pageHeight * 0.75 },
    score: { x: pageWidth * 0.85, y: pageHeight * 0.3 }
  };

  // Fond semi-transparent pour améliorer la lisibilité
  doc.setGState(new doc.GState({ opacity: 0.9 }));

  // Nom de l'étudiant (titre principal)
  doc.setTextColor(30, 30, 30);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(32);
  doc.text(data.studentName || 'Nom de l\'étudiant', positions.studentName.x, positions.studentName.y, {
    align: 'center'
  });

  // Nom du certificat
  doc.setTextColor(50, 50, 50);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(data.certificateName || 'Nom de la formation', positions.certificateName.x, positions.certificateName.y, {
    align: 'center'
  });

  // Date de délivrance
  doc.setTextColor(80, 80, 80);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  const issueDate = data.issueDate ? new Date(data.issueDate).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR');
  doc.text(`Délivré le: ${issueDate}`, positions.issueDate.x, positions.issueDate.y, {
    align: 'center'
  });

  // Nom du formateur
  doc.setTextColor(80, 80, 80);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.text(`Formateur: ${data.instructorName || 'Non spécifié'}`, positions.instructorName.x, positions.instructorName.y, {
    align: 'center'
  });

  // Date d'expiration
  if (data.expirationDate && data.expirationDate !== 'never') {
    const expDate = new Date(data.expirationDate).toLocaleDateString('fr-FR');
    doc.text(`Expire le: ${expDate}`, positions.expirationDate.x, positions.expirationDate.y, {
      align: 'center'
    });
  } else if (data.expirationDate === 'never' || !data.expirationDate) {
    doc.setTextColor(34, 139, 34);
    doc.setFont('helvetica', 'bold');
    doc.text('∞ Valide à vie', positions.expirationDate.x, positions.expirationDate.y, {
      align: 'center'
    });
  }

  // Score (si présent)
  if (typeof data.score === 'number' && !isNaN(data.score)) {
    doc.setTextColor(30, 30, 30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(`Score: ${Math.round(data.score)}/100`, positions.score.x, positions.score.y, {
      align: 'center'
    });
  }

  // ID du certificat
  if (data.id) {
    doc.setTextColor(120, 120, 120);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`ID: ${data.id}`, positions.certificateId.x, positions.certificateId.y, {
      align: 'center'
    });
  }

  // Remettre l'opacité à normale
  doc.setGState(new doc.GState({ opacity: 1.0 }));
};

/**
 * Génère un PDF de certificat avec un design élégant et professionnel (design par défaut)
 * @param {Object} certificationData - Données du certificat
 * @returns {jsPDF} Document PDF
 */
const generateDefaultCertificatePDF = (certificationData) => {
  // Normaliser les données entrantes avec des valeurs par défaut
  const data = {
    certificateType: 'certification',
    studentName: 'Étudiant',
    certificateName: 'Certificat de formation',
    issueDate: new Date().toISOString(),
    expirationDate: null,
    score: null,
    instructorName: 'Formateur',
    instructorId: '',
    id: '',
    remarks: '',
    ...certificationData
  };

  // Créer le document en paysage pour une finition premium
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  
  // Couleurs par type de certificat (RVB)
  const colors = {
    diploma: {
      primary: { r: 88, g: 28, b: 135 },      // Violet royal
      secondary: { r: 151, g: 71, b: 255 },   // Violet clair
      accent: { r: 218, g: 165, b: 32 }       // Or
    },
    certification: {
      primary: { r: 25, g: 54, b: 102 },      // Bleu marine
      secondary: { r: 68, g: 114, b: 196 },   // Bleu médium
      accent: { r: 218, g: 165, b: 32 }       // Or
    },
    attestation: {
      primary: { r: 0, g: 104, b: 74 },       // Vert foncé
      secondary: { r: 46, g: 139, b: 87 },    // Vert médium
      accent: { r: 218, g: 165, b: 32 }       // Or
    }
  };
  
  // Sélectionner le thème de couleur
  const theme = colors[data.certificateType] || colors.certification;
  
  // ===== FOND ET BORDURE =====
  
  // Fond blanc
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // Bordure décorative externe
  const margin = 10;
  doc.setDrawColor(theme.primary.r, theme.primary.g, theme.primary.b);
  doc.setLineWidth(1.5);
  doc.rect(margin, margin, pageWidth - 2*margin, pageHeight - 2*margin, 'D');
  
  // Bordure dorée interne
  doc.setDrawColor(theme.accent.r, theme.accent.g, theme.accent.b);
  doc.setLineWidth(0.75);
  doc.rect(margin + 4, margin + 4, pageWidth - 2*(margin + 4), pageHeight - 2*(margin + 4), 'D');
  
  // Ornements aux coins (motifs décoratifs)
  const cornerSize = 25;
  doc.setDrawColor(theme.accent.r, theme.accent.g, theme.accent.b);
  doc.setLineWidth(0.75);
  
  // Fonction pour dessiner un coin ornemental
  const drawCorner = (x, y, orientation) => {
    if (orientation === 'top-left') {
      doc.line(x, y + cornerSize/3, x, y);
      doc.line(x, y, x + cornerSize/3, y);
      doc.line(x, y + cornerSize, x + cornerSize/2, y + cornerSize/2);
      doc.line(x + cornerSize, y, x + cornerSize/2, y + cornerSize/2);
    }
    else if (orientation === 'top-right') {
      doc.line(x - cornerSize/3, y, x, y);
      doc.line(x, y, x, y + cornerSize/3);
      doc.line(x - cornerSize, y, x - cornerSize/2, y + cornerSize/2);
      doc.line(x, y + cornerSize, x - cornerSize/2, y + cornerSize/2);
    }
    else if (orientation === 'bottom-left') {
      doc.line(x, y - cornerSize/3, x, y);
      doc.line(x, y, x + cornerSize/3, y);
      doc.line(x, y - cornerSize, x + cornerSize/2, y - cornerSize/2);
      doc.line(x + cornerSize, y, x + cornerSize/2, y - cornerSize/2);
    }
    else if (orientation === 'bottom-right') {
      doc.line(x - cornerSize/3, y, x, y);
      doc.line(x, y, x, y - cornerSize/3);
      doc.line(x - cornerSize, y, x - cornerSize/2, y - cornerSize/2);
      doc.line(x, y - cornerSize, x - cornerSize/2, y - cornerSize/2);
    }
  };
  
  // Dessiner les quatre coins ornementaux
  drawCorner(margin + 6, margin + 6, 'top-left');
  drawCorner(pageWidth - margin - 6, margin + 6, 'top-right');
  drawCorner(margin + 6, pageHeight - margin - 6, 'bottom-left');
  drawCorner(pageWidth - margin - 6, pageHeight - margin - 6, 'bottom-right');
  
  // ===== ENTÊTE AVEC BANNIÈRE =====
  
  // Bannière principale
  const bannerY = margin + 18;
  const bannerHeight = 30;
  const bannerWidth = pageWidth - 2*margin - 40;
  const bannerX = (pageWidth - bannerWidth) / 2;
  
  // Fond dégradé de la bannière
  doc.setFillColor(theme.primary.r, theme.primary.g, theme.primary.b);
  doc.rect(bannerX, bannerY, bannerWidth, bannerHeight, 'F');
  
  // Triangles décoratifs
  doc.setFillColor(theme.accent.r, theme.accent.g, theme.accent.b);
  
  // Triangle gauche
  doc.triangle(
    bannerX - 8, bannerY + bannerHeight/2, 
    bannerX, bannerY, 
    bannerX, bannerY + bannerHeight
  );
  
  // Triangle droit
  doc.triangle(
    bannerX + bannerWidth + 8, bannerY + bannerHeight/2,
    bannerX + bannerWidth, bannerY,
    bannerX + bannerWidth, bannerY + bannerHeight
  );
  
  // Type de certificat en majuscules
  const typeLabel = data.certificateType === 'diploma' 
    ? 'DIPLÔME' 
    : data.certificateType === 'attestation' 
      ? 'ATTESTATION' 
      : 'CERTIFICATION';
  
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(26);
  doc.text(typeLabel, pageWidth/2, bannerY + bannerHeight/2, { 
    align: 'center', 
    baseline: 'middle' 
  });
  
  // ===== FILIGRANE =====
  
  // Grand filigrane en arrière-plan
  doc.setGState(new doc.GState({ opacity: 0.06 }));
  doc.setTextColor(theme.primary.r, theme.primary.g, theme.primary.b);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(100);
  doc.text(String(data.organizationName || 'FORMAPRO').toUpperCase(), pageWidth/2, pageHeight/2 + 10, {
    align: 'center', 
    baseline: 'middle' 
  });
  doc.setGState(new doc.GState({ opacity: 1.0 }));
  
  // ===== CONTENU PRINCIPAL =====
  
  // Phrase d'introduction
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(14);
  doc.text('Ce document certifie que', pageWidth/2, bannerY + bannerHeight + 30, { 
    align: 'center' 
  });
  
  // Nom de l'étudiant(e) en grand et en couleur
  doc.setTextColor(theme.primary.r, theme.primary.g, theme.primary.b);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(38);
  const studentName = String(data.studentName || '').trim() || '—';
  doc.text(studentName, pageWidth/2, bannerY + bannerHeight + 55, { 
    align: 'center' 
  });
  
  // Trait décoratif sous le nom
  const nameWidth = doc.getTextWidth(studentName) * 0.6;
  doc.setDrawColor(theme.accent.r, theme.accent.g, theme.accent.b);
  doc.setLineWidth(0.75);
  doc.line(
    pageWidth/2 - nameWidth/2, 
    bannerY + bannerHeight + 60, 
    pageWidth/2 + nameWidth/2, 
    bannerY + bannerHeight + 60
  );
  
  // Phrase de réussite
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(14);
  doc.text('a complété avec succès', pageWidth/2, bannerY + bannerHeight + 75, { 
    align: 'center' 
  });
  
  // Titre de la formation/certification
  doc.setTextColor(theme.primary.r, theme.primary.g, theme.primary.b);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  const title = String(data.certificateName || '').trim() || '—';
  doc.text(title, pageWidth/2, bannerY + bannerHeight + 95, { 
    align: 'center' 
  });
  
  // Trait décoratif sous le titre
  const titleWidth = Math.min(doc.getTextWidth(title) * 0.8, pageWidth * 0.7);
  doc.setDrawColor(theme.accent.r, theme.accent.g, theme.accent.b);
  doc.setLineWidth(1);
  doc.line(
    pageWidth/2 - titleWidth/2, 
    bannerY + bannerHeight + 102, 
    pageWidth/2 + titleWidth/2, 
    bannerY + bannerHeight + 102
  );
  
  // ===== INFORMATIONS DÉTAILLÉES =====
  
  // Grille d'informations (3 colonnes)
  const infoStartY = bannerY + bannerHeight + 120;
  const colGap = 15;
  const colWidth = (pageWidth - 2*margin - 2*colGap) / 3;
  
  // Fonctions utilitaires pour le formatage des dates
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  };
  
  // Configuration des colonnes d'information
  doc.autoTable({
    startY: infoStartY,
    margin: { left: margin, right: margin },
    tableWidth: pageWidth - 2*margin,
    columnStyles: {
      0: { cellWidth: colWidth },
      1: { cellWidth: colWidth },
      2: { cellWidth: colWidth }
    },
    body: [[
      {
        content: [
          { text: 'Étudiant', style: 'header' },
          { text: studentName, style: 'value' },
          data.studentId ? { text: `ID: ${data.studentId}`, style: 'subvalue' } : ''
        ].filter(Boolean)
      },
      {
        content: [
          { text: 'Formateur', style: 'header' },
          { text: data.instructorName || '—', style: 'value' },
          data.instructorId ? { text: `ID: ${data.instructorId}`, style: 'subvalue' } : ''
        ].filter(Boolean)
      },
      {
        content: [
          { text: 'Dates', style: 'header' },
          { text: `Délivré: ${formatDate(data.issueDate)}`, style: 'value' },
          data.expirationDate ? { text: `Expire: ${formatDate(data.expirationDate)}`, style: 'subvalue' } : ''
        ].filter(Boolean)
      }
    ]],
    styles: {
      cellPadding: 6,
      fontSize: 10,
      lineColor: [220, 220, 220],
      lineWidth: 0.5
    },
    columnStyles: {
      0: { fillColor: [250, 250, 252] },
      1: { fillColor: [250, 250, 252] },
      2: { fillColor: [250, 250, 252] }
    },
    didDrawCell: function(data) {
      // Personnalisation du rendu des cellules
      if (data.section === 'body') {
        const cell = data.cell;
        const content = cell.raw.content;
        const x = cell.x;
        const y = cell.y;
        
        if (content && Array.isArray(content)) {
          let offsetY = 0;
          
          content.forEach((item, idx) => {
            if (!item || !item.text) return;
            
            if (item.style === 'header') {
              doc.setTextColor(theme.primary.r, theme.primary.g, theme.primary.b);
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(11);
              offsetY += 5;
            } 
            else if (item.style === 'value') {
              doc.setTextColor(60, 60, 60);
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(12);
              offsetY += 18;
            }
            else if (item.style === 'subvalue') {
              doc.setTextColor(120, 120, 120);
              doc.setFont('helvetica', 'italic');
              doc.setFontSize(9);
              offsetY += 14;
            }
            
            doc.text(item.text, x + 8, y + offsetY);
          });
          
          // Trait coloré sur le dessus de chaque cellule
          doc.setDrawColor(theme.secondary.r, theme.secondary.g, theme.secondary.b);
          doc.setLineWidth(2);
          doc.line(x, y, x + cell.width, y);
        }
      }
    },
    drawCell: function(cell, data) {
      // Empêcher le dessin automatique du contenu des cellules
      return false;
    }
  });
  
  // ===== BADGE DE SCORE (SI FOURNI) =====
  
  if (typeof data.score === 'number' && !isNaN(data.score)) {
    // Position du badge de score
    const scoreX = pageWidth - margin - 35;
    const scoreY = bannerY + bannerHeight + 50;
    const radius = 22;
    
    // Cercle extérieur
    doc.setDrawColor(theme.primary.r, theme.primary.g, theme.primary.b);
    doc.setFillColor(255, 255, 255);
    doc.setLineWidth(1.5);
    doc.circle(scoreX, scoreY, radius, 'FD');
    
    // Cercle intérieur
    doc.setDrawColor(theme.accent.r, theme.accent.g, theme.accent.b);
    doc.setLineWidth(0.8);
    doc.circle(scoreX, scoreY, radius - 3, 'D');
    
    // Score
    doc.setTextColor(theme.primary.r, theme.primary.g, theme.primary.b);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.text(`${Math.round(data.score)}`, scoreX, scoreY - 3, {
      align: 'center',
      baseline: 'middle'
    });
    
    // /100
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('/100', scoreX, scoreY + 10, {
      align: 'center',
      baseline: 'middle'
    });
  }
  
  // ===== SIGNATURES ET SCEAU =====
  
  // Zone pour les signatures
  const sigY = pageHeight - margin - 45;
  const sigWidth = 80;
  
  // Fonction pour dessiner une signature
  const drawSignatureLine = (x, label) => {
    doc.setDrawColor(150, 150, 150);
    doc.setLineWidth(0.5);
    doc.line(x, sigY, x + sigWidth, sigY);
    
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text(label, x + sigWidth/2, sigY + 6, {
      align: 'center'
    });
  };
  
  // Dessiner les lignes de signature
  drawSignatureLine(margin + 30, 'Signature du Formateur');
  drawSignatureLine(pageWidth - margin - sigWidth - 30, 'Direction');
  
  // Sceau officiel au centre
  const sealX = pageWidth / 2;
  const sealY = pageHeight - margin - 35;
  
  // Cercles concentriques pour le sceau
  doc.setDrawColor(theme.primary.r, theme.primary.g, theme.primary.b);
  doc.setLineWidth(1);
  doc.circle(sealX, sealY, 18, 'D');
  
  doc.setDrawColor(theme.accent.r, theme.accent.g, theme.accent.b);
  doc.setLineWidth(0.8);
  doc.circle(sealX, sealY, 15, 'D');
  
  doc.setDrawColor(theme.primary.r, theme.primary.g, theme.primary.b);
  doc.setLineWidth(0.5);
  doc.circle(sealX, sealY, 12, 'D');
  
  // Texte du sceau
  doc.setTextColor(theme.primary.r, theme.primary.g, theme.primary.b);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('VALIDÉ', sealX, sealY - 2, {
    align: 'center'
  });
  
  // Année dans le sceau
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const currentYear = new Date().getFullYear();
  doc.text(`${currentYear}`, sealX, sealY + 6, {
    align: 'center'
  });
  
  // Remarques (si présentes)
  if (data.remarks && data.remarks.trim()) {
    const remarksY = pageHeight - margin - 75;
    const remarksWidth = pageWidth - (margin * 2 + 60);
    const remarksX = (pageWidth - remarksWidth) / 2;
    
    // Zone des remarques
    doc.setFillColor(250, 250, 252);
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.5);
    doc.roundedRect(remarksX, remarksY, remarksWidth, 25, 3, 3, 'FD');
    
    // Titre des remarques
    doc.setTextColor(theme.primary.r, theme.primary.g, theme.primary.b);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.text('Remarques:', remarksX + 5, remarksY + 8);
    
    // Contenu des remarques
    doc.setTextColor(80, 80, 80);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    
    const lines = doc.splitTextToSize(data.remarks, remarksWidth - 10);
    const maxLines = 2;
    const displayLines = lines.slice(0, maxLines);
    
    displayLines.forEach((line, idx) => {
      doc.text(line, remarksX + 5, remarksY + 16 + (idx * 10));
    });
  }
  
  // ===== PIED DE PAGE =====
  
  // Pied de page avec identifiant
  const footerY = pageHeight - margin - 10;
  
  doc.setTextColor(120, 120, 120);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  
  const idLabel = data.id ? `ID: ${data.id}` : '';
  const orgLabel = data.organizationName || 'FORMAPRO';
  const footerText = `${idLabel}${idLabel ? '  ·  ' : ''}Généré par ${orgLabel}`;
  
  doc.text(footerText.trim(), pageWidth/2, footerY, {
    align: 'center'
  });

  return doc;
};

/**
 * Télécharge un certificat au format PDF
 * @param {Object} certificationData - Données du certificat
 */
export const downloadCertificatePDF = async (certificationData) => {
  try {
    const doc = await generateCertificatePDF(certificationData);
    const filename = `Certificat_${certificationData.studentName.replace(/\s+/g, '_')}.pdf`;
    doc.save(filename);
    console.log(`Certificat généré: ${filename}`);
    return true;
  } catch (error) {
    console.error('Erreur lors de la génération du certificat:', error);
    return false;
  }
};